import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import DataTable from '../common/DataTable';
import DateTimeDisplay from '../common/DateTimeDisplay';
import useKnowledgeIndexing from '../../hooks/useKnowledgeIndexing.js';
import LoadingView from '../../../shared/components/LoadingView';
import { WorkspacePanel } from '../layout/WorkspaceView';
import StatusPill from '../../../shared/components/pill/StatusPill';
import LabelPill from '../../../shared/components/pill/LabelPill';
import CountPill from '../../../shared/components/pill/CountPill';
import { knowledgeIndexStatus } from '../../utils/mediaStatus';

// Logs are timestamped monospace lines, so they need more room than a standard side panel.
const LOGS_WIDTH = 520;

function formatLogTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function queueLabel(queueStatus) {
  if (!queueStatus) return null;
  const labels = {
    running: 'Job running',
    queued: 'Queued',
    scheduled: 'Scheduled',
    pending: 'In queue',
  };
  return labels[queueStatus] || queueStatus;
}

function LogPanel({ detail, loading }) {
  if (loading && !detail) {
    return (
      <div className="text-muted small py-4 text-center">Loading logs…</div>
    );
  }

  if (!detail) {
    return (
      <div className="text-muted small py-4 text-center">
        Select a document to view indexing logs.
      </div>
    );
  }

  const logs = detail.logs || [];

  return (
    <div className="d-flex flex-column h-100">
      <div className="border-bottom pb-2 mb-2">
        <div className="fw-semibold">{detail.filename}</div>
        <div className="text-muted small">
          {detail.asset_id ? detail.asset_name : 'Organization-wide'}
          {detail.stuck_reason && (
            <span className="text-warning d-block mt-1">{detail.stuck_reason}</span>
          )}
          {detail.knowledge_index_error && (
            <span className="text-danger d-block mt-1">{detail.knowledge_index_error}</span>
          )}
        </div>
      </div>
      <div
        className="flex-grow-1 overflow-auto font-monospace small bg-dark text-light rounded p-3"
        style={{ minHeight: 280, maxHeight: 480 }}
      >
        {logs.length === 0 ? (
          <span className="text-secondary">No log entries yet.</span>
        ) : (
          logs.map((entry) => (
            <div key={entry.id} className="mb-2">
              <span className="text-secondary me-2">{formatLogTime(entry.created_at)}</span>
              {entry.level !== 'info' && (
                <span
                  className={
                    entry.level === 'error'
                      ? 'text-danger me-1'
                      : 'text-warning me-1'
                  }
                >
                  [{entry.level}]
                </span>
              )}
              <span>{entry.message}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ReindexControls({ detail, busy, onReindex }) {
  if (!detail) return null;

  return (
    <div className="border-bottom pb-3 mb-3">
      {/* The filename and asset are shown by the log panel immediately below, so this row
          carries only what that cannot: the stored OCR choice and the actions. */}
      <div className="d-flex flex-wrap align-items-center gap-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          disabled={busy}
          onClick={() => onReindex(detail.media_id, { ocr: false })}
        >
          <i className="bi bi-arrow-clockwise me-1" />
          Re-index
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={busy}
          onClick={() => onReindex(detail.media_id, { ocr: true })}
          title="Reads text out of scanned pages and images. Much slower, but the only way to index a scanned document."
        >
          Re-index with OCR
        </button>
        <LabelPill tone={detail.knowledge_index_ocr ? 'info' : 'quiet'} className="ms-auto">
          OCR {detail.knowledge_index_ocr ? 'on' : 'off'}
        </LabelPill>
      </div>
    </div>
  );
}

ReindexControls.propTypes = {
  detail: PropTypes.object,
  busy: PropTypes.bool,
  onReindex: PropTypes.func.isRequired,
};

ReindexControls.defaultProps = { detail: null, busy: false };

export default function AiIndexingPanel() {
  const [selectedMediaId, setSelectedMediaId] = useState(null);
  const {
    active,
    recent,
    workerHealth,
    detail,
    loading,
    detailLoading,
    error,
    actionLoading,
    actionResult,
    clearActionResult,
    retryFailed,
    reconcileDocling,
    reindex,
  } = useKnowledgeIndexing({
    selectedMediaId,
  });

  const failedCount = useMemo(
    () => recent.filter((item) => item.knowledge_index_status === 'failed').length,
    [recent]
  );

  const indexingColumns = useMemo(
    () => [
      {
        key: 'knowledge_index_status',
        label: 'Status',
        sortable: true,
        render: (item) => {
          const badge = knowledgeIndexStatus(item.knowledge_index_status);
          const queue = queueLabel(item.queue_status);
          return (
            <>
              <StatusPill tone={badge.tone}>{badge.label}</StatusPill>
              {queue && <StatusPill tone="quiet" className="ms-1">{queue}</StatusPill>}
            </>
          );
        },
        filterValue: (item) =>
          [item.knowledge_index_status, item.queue_status].filter(Boolean).join(' '),
      },
      {
        key: 'asset_name',
        label: 'Asset',
        sortable: true,
        render: (item) => (
          item.asset_id ? (
            <Link
              to={`/admin/assets/${item.asset_id}`}
              className="text-decoration-none"
              onClick={(e) => e.stopPropagation()}
            >
              {item.asset_name}
            </Link>
          ) : (
            <span className="text-muted">Organization-wide</span>
          )
        ),
        filterValue: (item) => item.asset_name || 'Organization-wide',
      },
      {
        key: 'filename',
        label: 'File',
        sortable: true,
        className: 'text-truncate',
        style: { maxWidth: 220 },
        render: (item) => (
          <span title={item.filename}>{item.filename}</span>
        ),
        filterValue: (item) => item.filename || '',
      },
      {
        key: 'updated_at',
        label: 'Updated',
        sortable: true,
        render: (item) => (
          <span className="text-muted small">
            <DateTimeDisplay isoString={item.updated_at} />
          </span>
        ),
        filterValue: (item) => item.updated_at || '',
      },
    ],
    []
  );

  const renderStuckHint = (row, { selected }) =>
    row.stuck_reason && selected ? (
      <tr>
        <td colSpan={indexingColumns.length} className="small text-warning-emphasis bg-warning-subtle border-0 pt-0 pb-2">
          <i className="bi bi-exclamation-triangle me-1" />
          {row.stuck_reason}
        </td>
      </tr>
    ) : null;

  React.useEffect(() => {
    const allRows = [...active, ...recent];
    const seen = new Set();
    const first = allRows.find((item) => {
      if (seen.has(item.media_id)) return false;
      seen.add(item.media_id);
      return true;
    });
    if (!selectedMediaId && first) {
      setSelectedMediaId(first.media_id);
    }
  }, [active, recent, selectedMediaId]);

  const busy = actionLoading != null;

  const lists = (
    <>
      <div className="mb-3">
        <p className="text-muted small mb-0">
          Shared PDF, HTML, text, CSV, and Excel files are converted and embedded for
          field-engineer AI chat. This refreshes every few seconds while indexing is in progress.
        </p>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          {error}
        </div>
      )}

      {actionResult && (
        <div className="alert alert-info alert-dismissible" role="status">
          <button type="button" className="btn-close" aria-label="Close" onClick={clearActionResult} />
          {actionResult.type === 'retry_failed' && (
            <>
              <strong>Retry queued.</strong>{' '}
              {actionResult.retried_count} document(s) re-queued
              {actionResult.skipped_count > 0 && `, ${actionResult.skipped_count} skipped (not eligible).`}
            </>
          )}
          {actionResult.type === 'reindex' && (
            <>
              <strong>Re-index queued.</strong>{' '}
              {actionResult.filename} — OCR {actionResult.ocr ? 'on' : 'off'}.
            </>
          )}
          {actionResult.type === 'reconcile_docling' && (
            <>
              <strong>Docling check complete.</strong>{' '}
              {actionResult.restored_to_processing_count} restored,{' '}
              {actionResult.requeued_count} re-queued,{' '}
              {actionResult.skipped_count} unchanged
              {actionResult.error_count > 0 && `, ${actionResult.error_count} error(s) — see logs.`}
            </>
          )}
        </div>
      )}

      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className="btn btn-outline-primary btn-sm"
          disabled={busy || failedCount === 0}
          onClick={retryFailed}
        >
          <i className="bi bi-arrow-repeat me-1" />
          Retry all failed{failedCount > 0 ? ` (${failedCount})` : ''}
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          disabled={busy || failedCount === 0}
          onClick={reconcileDocling}
          title="Poll Docling for failed items that still have a task id in the logs"
        >
          <i className="bi bi-cloud-check me-1" />
          Check Docling status for failed
        </button>
      </div>

      {workerHealth && !workerHealth.workers_alive && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2" />
          <strong>No job worker detected.</strong>{' '}
          {workerHealth.solid_queue_in_puma === 'true'
            ? 'Production runs Solid Queue inside Puma — restart the web app so the plugin starts.'
            : 'Run bin/jobs alongside the web app, or enable SOLID_QUEUE_IN_PUMA.'}
          {workerHealth.pending_knowledge_index_jobs > 0 && (
            <span className="d-block mt-1 small mb-0">
              {workerHealth.pending_knowledge_index_jobs} indexing job(s) waiting in the queue.
            </span>
          )}
        </div>
      )}

      <section className="mb-4">
        <h6 className="text-muted fw-normal mb-2">
          Active{active.length > 0 && <CountPill className="ms-2">{active.length}</CountPill>}
        </h6>
        <DataTable
          items={active}
          columns={indexingColumns}
          filterPlaceholder="Search active jobs…"
          emptyMessage="No indexing in progress."
          hideFilter={active.length < 5}
          getRowKey={(item) => item.media_id}
          selectedRowKey={selectedMediaId}
          onRowClick={(item) => setSelectedMediaId(item.media_id)}
          tableClassName="table-hover"
          renderAfterRow={renderStuckHint}
          className="mb-0"
        />
      </section>

      <section>
        <h6 className="text-muted fw-normal mb-2">Recent (7 days)</h6>
        <DataTable
          items={recent}
          columns={indexingColumns}
          filterPlaceholder="Search recent jobs…"
          emptyMessage="No recently completed indexing."
          hideFilter={recent.length < 5}
          getRowKey={(item) => item.media_id}
          selectedRowKey={selectedMediaId}
          onRowClick={(item) => setSelectedMediaId(item.media_id)}
          tableClassName="table-hover"
          className="mb-0"
        />
      </section>
    </>
  );

  return (
    <>
      <WorkspacePanel title="Documents" grow>
        {loading && active.length === 0 && recent.length === 0 ? (
          <LoadingView
            symbolClass="bi-robot"
            title="Loading indexing status"
            subtitle="Fetching active and recent indexing jobs."
            className="py-5"
          />
        ) : (
          lists
        )}
      </WorkspacePanel>

      <WorkspacePanel title="Logs" width={LOGS_WIDTH} side="right">
        <ReindexControls detail={detail} busy={busy} onReindex={reindex} />
        <LogPanel detail={detail} loading={detailLoading} />
      </WorkspacePanel>
    </>
  );
}
